import { Injectable } from '@angular/core';
import { HttpUtility, HandleError, HttpErrorHandler } from '@core/http';
import { UserClinicalCredentials } from '@core/model';
import { ODataResponse, ProductNames } from '@mynexus/mynexus-ui-lib';
import { Observable, of } from 'rxjs';
import { catchError, map, publishReplay, refCount, shareReplay } from 'rxjs/operators';
import { IUserClaims, ShellService } from '@mynexus/mfe-core';
import { UrlConstants } from '../constants/url-constants';
import { CredentialsByEmails } from '../model/credentials-by-emails';

@Injectable()
export class AuthenticationService {
  private apiBaseUrl: string;
  private userProfileCredentialsCache = new Map<string, Observable<string>>();
  private userCredentialsCache = new Map<string, Observable<UserClinicalCredentials>>();
  public handleError: HandleError;

  constructor(
    private httpUtility: HttpUtility,
    private httpErrorHandler: HttpErrorHandler,
    private readonly shellService: ShellService
  ) {
    this.handleError = this.httpErrorHandler.createHandleError('AuthenticationService');
    this.apiBaseUrl = this.shellService.getBaseUrlFor(ProductNames.HomeHealth);
  }

  get userEmail(): string | null {
    return this.shellService.authenticationService.userEmail;
  }

  get userName(): string {
    // tslint:disable-next-line: no-non-null-assertion
    return this.shellService.authenticationService.userName || '';
  }

  get claims(): IUserClaims | null {
    return this.shellService.authenticationService.userClaims;
  }

  get groups(): string[] | undefined {
    return this.shellService.authenticationService.userClaims?.groups;
  }

  get isExternalUser(): boolean {
    if (this.claims) {
      return !!this.claims.extension_isExternal;
    }

    return false;
  }

  getUserProfileCredentialsByEmail(email: string): Observable<string> | undefined {
    if (!this.userProfileCredentialsCache.has(email)) {
      const url = `${this.apiBaseUrl}/ProfileCredentials`;
      const userClinicalCred = this.httpUtility
        .post<ODataResponse<UserClinicalCredentials>>(url, [email])
        .pipe(
          publishReplay(1),
          refCount(),
          map((data: ODataResponse<UserClinicalCredentials>) => {
            const user = data.result[0];
            let userCred = email;
            if (user && (user.firstName || user.lastName)) {
              userCred = `${user.firstName} ${user.lastName}${user.credentials ? ',' : ''} ${user.credentials || ''
                }`;
            }
            return userCred;
          }),
          catchError(() => of(email))
        );
      this.userProfileCredentialsCache.set(email, userClinicalCred);
    }
    return this.userProfileCredentialsCache.get(email);
  }

  getUserProfileCredentialsByEmails(emails: string[]): Observable<UserClinicalCredentials[]> {
    const url = `${this.apiBaseUrl}/${UrlConstants.userProfilesListCredentialsByEmails}`;
    return this.httpUtility.post<UserClinicalCredentials[]>(url, emails);
  }

  fetchUserProfileCredentialsByEmail(email: string): Observable<UserClinicalCredentials> {
    const url = `${this.apiBaseUrl}/${UrlConstants.userProfileCredentialsByEmail}/${email}`;

    return this.httpUtility.get<UserClinicalCredentials>(url);
  }

  getUserClinicalCredentialsByEmail(email: string): Observable<UserClinicalCredentials> {
    if (!this.userCredentialsCache.has(email)) {
      const userClinicalCred = this.fetchUserProfileCredentialsByEmail(email).pipe(
        shareReplay()
      );

      this.userCredentialsCache.set(email, userClinicalCred);
    }

    return this.userCredentialsCache.get(email);
  }

  getMappedUserProfileCredentialsByEmails(emails: string[]): Observable<CredentialsByEmails> {
    return this.getUserProfileCredentialsByEmails(emails).pipe(
      map((credentials: UserClinicalCredentials[]): CredentialsByEmails => {
        return credentials.reduce((acc, cur) => {
          if (cur.firstName || cur.lastName) {
            acc[cur.email] = `${cur.firstName} ${cur.lastName}${cur.credentials ? ',' : ''} ${cur.credentials || ''}`;
          }
          return acc;
        }, {});
      }),
      catchError(() => of({}))
    );
  }
}
